import { powerUpAtlas } from '../animation/Theme';
import Phaser from 'phaser';
import { TILE } from '../constants';
import { PowerUpType } from '../types';
import { POWERUP_COLORS, POWERUP_LABELS } from '../constants';

// Bob animation settings
const BOB_AMPLITUDE = 4;        // pixels up/down
const BOB_FREQUENCY = 2.5;      // cycles per second
const GLOW_MIN_ALPHA = 0.25;
const GLOW_MAX_ALPHA = 0.75;

export class PowerUp extends Phaser.GameObjects.Container {
  type: PowerUpType;
  col: number;
  row: number;
  collected: boolean;

  bobTimer: number;

  private gfx: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  private glowRing: Phaser.GameObjects.Graphics;
  private baseY: number;

  constructor(scene: Phaser.Scene, col: number, row: number, type: PowerUpType) {
    // Container centered on tile
    const cx = col * TILE + TILE / 2;
    const cy = row * TILE + TILE / 2;
    super(scene, cx, cy);

    this.type = type;
    this.col = col;
    this.row = row;
    this.collected = false;
    this.bobTimer = 0;
    this.baseY = cy;

    // Build visuals (back-to-front: glow, body, label)
    this.glowRing = scene.add.graphics();
    this.gfx      = scene.add.graphics();
    this.label    = scene.add.text(0, 0, '', {
      fontSize: '18px',
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'center',
    });
    this.label.setOrigin(0.5, 0.5);

    this.add([this.glowRing, this.gfx, this.label]);

    const icon = scene.add.image(0, 0, powerUpAtlas(type), `powerups/${type}`);
    icon.setOrigin(0.5, 0.5).setScale(0.5);
    this.add(icon);
    scene.add.existing(this);
  }

  // ---------------------------------------------------------------------------
  // Visual construction
  // ---------------------------------------------------------------------------

  private drawPowerUp(): void {
    const color = POWERUP_COLORS[this.type] ?? 0xffffff;
    const half  = TILE / 2 - 6;

    // Glow ring (redrawn each frame for pulse)
    this.drawGlowRing(color, half, GLOW_MIN_ALPHA);

    const g = this.gfx;
    g.clear();

    // Drop shadow
    g.fillStyle(0x000000, 0.35);
    g.fillRoundedRect(-half + 3, -half + 3, half * 2, half * 2, 10);

    // Dark background box
    g.fillStyle(0x111122, 1);
    g.fillRoundedRect(-half, -half, half * 2, half * 2, 10);

    // Colored top highlight strip (like frame from reference)
    g.fillStyle(color, 1);
    g.fillRoundedRect(-half, -half, half * 2, 8, { tl: 10, tr: 10, bl: 0, br: 0 });

    // Inner area (dark panel)
    g.fillStyle(0x1a1a2e, 1);
    g.fillRoundedRect(-half + 3, -half + 8, half * 2 - 6, half * 2 - 12, 6);

    // Colored border frame
    g.lineStyle(3, color, 0.85);
    g.strokeRoundedRect(-half, -half, half * 2, half * 2, 10);

    // Inner highlight (top-left shine on border)
    g.fillStyle(0xffffff, 0.15);
    g.fillRoundedRect(-half + 1, -half + 1, half - 2, (half - 2) * 0.5, 8);

    // Draw icon inside (no text)
    this.drawIcon(g, color);

    // Hide the text label (we use procedural icons)
    this.label.setText('');
  }

  private drawIcon(g: Phaser.GameObjects.Graphics, color: number): void {
    const s = 9; // icon scale base
    switch (this.type) {
      case PowerUpType.EXTRA_BOMB:
        // Black bomb shape
        g.fillStyle(0x111111, 1);
        g.fillCircle(0, 4, s);
        g.fillStyle(0x444444, 1);
        g.fillCircle(-3, 1, 4);
        // Fuse
        g.lineStyle(2, 0x888844, 1);
        g.beginPath(); g.moveTo(3, -4); g.lineTo(7, -9); g.strokePath();
        g.fillStyle(0xffaa00, 1);
        g.fillCircle(7, -9, 2.5);
        break;

      case PowerUpType.FIRE_UP:
        // Flame shape
        g.fillStyle(0xff4400, 1);
        g.fillEllipse(0, 5, 14, 18);
        g.fillStyle(0xff8800, 1);
        g.fillEllipse(0, 5, 10, 14);
        g.fillStyle(0xffee00, 1);
        g.fillEllipse(0, 6, 5, 8);
        // Small flame tip going up
        g.fillStyle(0xff4400, 1);
        g.fillEllipse(-3, -4, 5, 10);
        g.fillEllipse( 3, -2, 5, 8);
        g.fillStyle(0xff8800, 1);
        g.fillEllipse(-3, -3, 3, 7);
        break;

      case PowerUpType.SPEED_UP:
        // Boot/sneaker shape (simplified)
        g.fillStyle(color, 1);
        // Shoe body
        g.fillRoundedRect(-7, -1, 12, 8, 3);
        // Sole
        g.fillStyle(0xffffff, 1);
        g.fillRoundedRect(-8, 7, 14, 4, 2);
        // Speed arrow
        g.fillStyle(0xffffff, 1);
        g.fillRect(-2, -9, 2, 8);
        g.beginPath();
        g.fillTriangle(-5, -9, 5, -9, 0, -14);
        break;

      case PowerUpType.BOMB_KICK:
        // Boot with kick lines
        g.fillStyle(color, 1);
        g.fillRoundedRect(-8, 2, 14, 9, 3);
        g.fillStyle(0xffffff, 1);
        g.fillRoundedRect(-9, 11, 16, 4, 2);
        // Impact lines
        g.lineStyle(2, 0xffff00, 0.9);
        g.beginPath(); g.moveTo(6, 4); g.lineTo(13, 0); g.strokePath();
        g.beginPath(); g.moveTo(6, 8); g.lineTo(14, 7); g.strokePath();
        g.beginPath(); g.moveTo(6, 12); g.lineTo(13, 14); g.strokePath();
        break;

      case PowerUpType.REMOTE_BOMB:
        // Bomb + radio waves
        g.fillStyle(0x111111, 1);
        g.fillCircle(-2, 6, 7);
        g.fillStyle(0x444444, 1);
        g.fillCircle(-4, 4, 3);
        g.lineStyle(2, color, 0.9);
        g.beginPath(); g.arc(6, -2, 5, -0.8, 0.8); g.strokePath();
        g.beginPath(); g.arc(6, -2, 9, -0.8, 0.8); g.strokePath();
        g.fillStyle(0xffaa00, 1);
        g.fillCircle(4, -9, 2);
        break;

      case PowerUpType.SHIELD:
        // Pentagon shield
        g.fillStyle(color, 1);
        g.fillStyle(0x2255aa, 1);
        g.beginPath();
        g.moveTo(0, -11);
        g.lineTo(9, -5);
        g.lineTo(7, 10);
        g.lineTo(0, 13);
        g.lineTo(-7, 10);
        g.lineTo(-9, -5);
        g.closePath();
        g.fillPath();
        g.fillStyle(color, 1);
        g.beginPath();
        g.moveTo(0, -8);
        g.lineTo(6, -3);
        g.lineTo(4, 7);
        g.lineTo(0, 10);
        g.lineTo(-4, 7);
        g.lineTo(-6, -3);
        g.closePath();
        g.fillPath();
        // Cross emblem
        g.fillStyle(0xffffff, 0.7);
        g.fillRect(-1, -4, 3, 12);
        g.fillRect(-5, 0, 11, 3);
        break;

      case PowerUpType.EXTRA_LIFE:
        // Heart shape (two circles + triangle)
        g.fillStyle(0xff2255, 1);
        g.fillCircle(-4, -2, 6);
        g.fillCircle( 4, -2, 6);
        g.beginPath();
        g.fillTriangle(-10, 1, 10, 1, 0, 12);
        g.fillStyle(0xff6688, 0.6);
        g.fillCircle(-4, -4, 3);
        break;

      case PowerUpType.FIRE_IMMUNITY:
        // Snowflake over flame
        g.fillStyle(0xff4400, 0.5);
        g.fillEllipse(0, 6, 12, 16);
        g.fillStyle(0x88ddff, 1);
        g.fillRect(-1, -9, 2, 18);
        g.fillRect(-9, -1, 18, 2);
        g.lineStyle(2, 0x88ddff, 1);
        g.beginPath(); g.moveTo(-7, -7); g.lineTo(7, 7); g.strokePath();
        g.beginPath(); g.moveTo(7, -7); g.lineTo(-7, 7); g.strokePath();
        // Star ends
        for (let a = 0; a < 6; a++) {
          const ang = (a / 6) * Math.PI * 2;
          g.fillStyle(0x88ddff, 1);
          g.fillCircle(Math.cos(ang) * 9, Math.sin(ang) * 9, 2);
        }
        break;

      case PowerUpType.WALL_PASS:
        // Ghost shape
        g.fillStyle(0xaaddff, 0.85);
        g.fillCircle(0, -2, 9);
        g.fillRect(-9, -2, 18, 11);
        // Scalloped bottom
        g.fillStyle(0x111122, 1);
        g.fillCircle(-6, 11, 4);
        g.fillCircle( 0, 11, 4);
        g.fillCircle( 6, 11, 4);
        // Eyes
        g.fillStyle(0x111122, 1);
        g.fillCircle(-3, -2, 3);
        g.fillCircle( 3, -2, 3);
        g.fillStyle(0xffffff, 0.5);
        g.fillCircle(-2, -3, 1.5);
        g.fillCircle( 4, -3, 1.5);
        break;

      case PowerUpType.BOMB_PASS:
        // Bomb with arrow through it
        g.fillStyle(0x111111, 0.6);
        g.fillCircle(0, 4, 8);
        // Arrow passing through
        g.fillStyle(0xffff00, 1);
        g.fillRect(-11, 2, 22, 4);
        g.beginPath();
        g.fillTriangle(10, -1, 10, 9, 15, 4);
        break;
    }
  }

  private drawGlowRing(color: number, half: number, alpha: number): void {
    const g = this.glowRing;
    g.clear();
    g.lineStyle(4, color, alpha);
    g.strokeRoundedRect(-half - 5, -half - 5, (half + 5) * 2, (half + 5) * 2, 14);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  update(delta: number): void {
    if (this.collected) return;

    this.bobTimer += delta / 1000; // convert to seconds

    // Bob: smooth sine wave offset applied to container Y
    const offsetY = Math.sin(this.bobTimer * BOB_FREQUENCY * Math.PI * 2) * BOB_AMPLITUDE;
    this.y = this.baseY + offsetY;

    // Glow pulse: alpha oscillates between min and max
    const glowAlpha =
      GLOW_MIN_ALPHA +
      ((Math.sin(this.bobTimer * BOB_FREQUENCY * Math.PI * 2) + 1) / 2) *
        (GLOW_MAX_ALPHA - GLOW_MIN_ALPHA);

    const color = POWERUP_COLORS[this.type] ?? 0xffffff;
    const half  = TILE / 2 - 6;
    this.drawGlowRing(color, half, glowAlpha);
  }

  collect(): void {
    if (this.collected) return;
    this.collected = true;

    // Stop bobbing at current position
    this.y = this.baseY;

    // Collect animation: expand + fade out
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.7,
      scaleY: 1.7,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.destroy();
      },
    });

    // Particle burst: several small circles flying outward
    const color = POWERUP_COLORS[this.type] ?? 0xffffff;
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const speed = 60 + Math.random() * 40;
      const particle = this.scene.add.graphics();
      particle.fillStyle(color, 1);
      particle.fillCircle(0, 0, 4 + Math.random() * 4);
      particle.x = this.x;
      particle.y = this.y;

      this.scene.tweens.add({
        targets: particle,
        x: particle.x + Math.cos(angle) * speed,
        y: particle.y + Math.sin(angle) * speed,
        alpha: 0,
        scaleX: 0,
        scaleY: 0,
        duration: 350 + Math.random() * 150,
        ease: 'Power2',
        onComplete: () => {
          particle.destroy();
        },
      });
    }
  }

  getGridPos(): { col: number; row: number } {
    return { col: this.col, row: this.row };
  }
}
